import { Link } from 'react-router-dom'

type Figures = {
  // Assignments below their target today, as "Intake (1 of 2)".
  short: string[]
  over: string[]
  pending: number
  overtimeHours: number
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

// A plain-English reading of figures the dashboard already holds, each part
// naming the page it comes from.
function summarize({ short, over, pending, overtimeHours }: Figures): string {
  const parts: string[] = []
  if (short.length > 0) parts.push(`${plural(short.length, 'assignment is', 'assignments are')} short today: ${short.join(', ')}.`)
  if (over.length > 0) parts.push(`${plural(over.length, 'assignment is', 'assignments are')} over target: ${over.join(', ')}.`)
  if (pending > 0) parts.push(`${plural(pending, 'request is', 'requests are')} waiting on your decision.`)
  if (overtimeHours > 0) parts.push(`${overtimeHours} hours of overtime are planned this pay period.`)
  return parts.length > 0 ? parts.join(' ') : 'Staffing meets its targets today and nothing is waiting on your decision.'
}

export default function InsightsSummary(figures: Figures) {
  const { short, over, pending, overtimeHours } = figures
  const tiles = [
    { label: 'Short today', value: short.length, to: '/supervisor/daily-assignments', warn: short.length > 0 },
    { label: 'Over target', value: over.length, to: '/supervisor/daily-assignments', warn: over.length > 0 },
    { label: 'Awaiting a decision', value: pending, to: '/supervisor/approval-inbox', warn: false },
    { label: 'Planned overtime', value: `${overtimeHours} hrs`, to: '/supervisor/reports/overtime-planning', warn: false },
  ]

  return (
    <section aria-label="Pay period at a glance" className="rounded-surface border border-line bg-canvas p-4 sm:p-5">
      <div className="max-w-[60ch]">
        <h2 className="text-body font-extrabold">This pay period at a glance</h2>
        <p className="mt-1.5 text-small text-ink-muted">{summarize(figures)}</p>
      </div>
      <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {tiles.map((tile) => (
          <li key={tile.label}>
            <Link to={tile.to} className="block rounded-control border border-line px-3 py-2.5 hover:bg-surface-2">
              <span className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{tile.label}</span>
              <span className={`mt-0.5 block text-h2 font-extrabold [font-variant-numeric:tabular-nums] ${tile.warn ? 'text-error' : ''}`}>{tile.value}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
